import { TICKET_PAYMENT } from "../config/global.configuration";
import type { CreditNoteSummaryDTO } from "../types/dtos/api/purchase/credit-note-summary.dto";
import type { ItineraryDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import type { ItineraryPricingPreferencesDTO } from "../types/dtos/api/pricing/itinerary-pricing-preferences.dto";
import type { PurchaseAttemptResumeResponseDTO } from "../types/dtos/api/purchase/purchase-attempt.dto";
import type {
    BookingRequestDTO,
    BookingResponseDTO,
} from "../types/dtos/api/purchase/purchase-booking.dto";
import type { PurchasedBookingDTO } from "../types/dtos/api/purchase/purchase-bookings.dto";
import type {
    CancelPaymentRequestDTO,
    CreatePaymentGatewayRequestDTO,
    CreatePaymentGatewayResponseDTO,
    PaymentMethodSummaryDTO,
    PaymentStatusResponseDTO,
    VerifyPaymentRequestDTO,
} from "../types/dtos/api/purchase/purchase-payment.dto";
import type {
    PrebookRequestDTO,
    PrebookResponseDTO,
} from "../types/dtos/api/purchase/purchase-prebook.dto";
import type {
    BookingRefundPreviewResponseDTO,
    BookingRefundResponseDTO,
    ConfirmBookingRefundRequestDTO,
} from "../types/dtos/api/purchase/purchase-refund.dto";
import type {
    BuildPurchaseSegmentsRequestDTO,
    BuildPurchaseSegmentsResponseDTO,
} from "../types/dtos/api/purchase/purchase-segments.dto";
import { authHttp } from "./auth/auth-http";

/**
 * Authenticated API client for the CFF ticket purchase workflow.
 *
 * Payment calls go through Payrexx: `postPurchasePaymentGateway` creates the
 * hosted checkout session, `postPurchasePaymentVerify` re-checks it against the
 * Payrexx API (never trust redirect params), `postPurchasePaymentCancel` aborts
 * a still-pending payment, and `getPurchaseAttempt` rebuilds local state after
 * a full-page/in-app-browser redirect.
 */
export class TicketPurchaseApi {

    /**
     * Builds the purchasable CFF segments of an itinerary.
     *
     * @param itinerary - Itinerary to price.
     * @param pricingPreferences - Optional pricing preferences.
     * @returns Purchasable segments and conversation id.
     */
    public static async postPurchaseSegments(
        itinerary: ItineraryDTO,
        pricingPreferences?: ItineraryPricingPreferencesDTO,
    ): Promise<BuildPurchaseSegmentsResponseDTO> {
        const payload: BuildPurchaseSegmentsRequestDTO = {
            itinerary,
            pricingPreferences,
        };
        const response = await authHttp.post<BuildPurchaseSegmentsResponseDTO>( "/api/purchase/segments", payload );

        return response.data;
    }

    /**
     * Creates CFF prebookings for the selected offers.
     *
     * @param request - Selected offers + itinerary.
     * @returns Prebook response with the purchase attempt id.
     */
    public static async postPurchasePrebook( request: PrebookRequestDTO ): Promise<PrebookResponseDTO> {
        const response = await authHttp.post<PrebookResponseDTO>( "/api/purchase/prebook", request );

        return response.data;
    }

    /**
     * Returns the shopper's saved one-click payment method, when any.
     *
     * A saved method is the « Paiement en un clic » opt-in signal: the wizard
     * preselects the `direct` checkout mode while it exists.
     *
     * @returns Display-safe method summary, or `null` when none is stored.
     */
    public static async getPurchasePaymentMethod(): Promise<PaymentMethodSummaryDTO | null> {
        const response = await authHttp.get<PaymentMethodSummaryDTO | null>( "/api/purchase/payment/method" );

        return response.data;
    }

    /**
     * Creates a Payrexx payment gateway for a prebooked attempt.
     *
     * @param request - Attempt id + client platform (`web`/`native`).
     * @returns Hosted page `redirectUrl` (or `succeeded` when already paid).
     */
    public static async postPurchasePaymentGateway( request: CreatePaymentGatewayRequestDTO ): Promise<CreatePaymentGatewayResponseDTO> {
        const response = await authHttp.post<CreatePaymentGatewayResponseDTO>( "/api/purchase/payment/gateway", request );

        return response.data;
    }

    /**
     * Re-checks a payment against the Payrexx API (authoritative status).
     *
     * @param request - Attempt id + optional payment id.
     * @returns Verified payment status.
     */
    public static async postPurchasePaymentVerify( request: VerifyPaymentRequestDTO ): Promise<PaymentStatusResponseDTO> {
        const response = await authHttp.post<PaymentStatusResponseDTO>( "/api/purchase/payment/verify", request );

        return response.data;
    }

    /**
     * Cancels a still-pending payment and deletes its Payrexx gateway.
     *
     * @param request - Attempt id.
     * @returns Resulting payment status.
     */
    public static async postPurchasePaymentCancel( request: CancelPaymentRequestDTO ): Promise<PaymentStatusResponseDTO> {
        const response = await authHttp.post<PaymentStatusResponseDTO>( "/api/purchase/payment/cancel", request );

        return response.data;
    }

    /**
     * Returns the resume snapshot of a purchase attempt (itinerary, prebooking,
     * latest payment) so the wizard can be rebuilt after a Payrexx redirect.
     *
     * @param purchaseAttemptId - Purchase attempt id.
     * @returns Resume snapshot.
     */
    public static async getPurchaseAttempt( purchaseAttemptId: string ): Promise<PurchaseAttemptResumeResponseDTO> {
        const response = await authHttp.get<PurchaseAttemptResumeResponseDTO>( `/api/purchase/attempts/${ purchaseAttemptId }` );

        return response.data;
    }

    /**
     * Books the prebooked offers (backend re-checks the Payrexx payment first).
     *
     * The request has its own timeout: the synchronous pipeline (CFF booking,
     * capture, commit, ticket-file downloads) takes longer than the shared 30 s
     * HTTP default. On timeout the caller polls `GET /purchase/attempts/:id` —
     * `/book` is never re-submitted.
     *
     * @param request - Booking request.
     * @returns Booking response with ticket metadata.
     */
    public static async postPurchaseBooking( request: BookingRequestDTO ): Promise<BookingResponseDTO> {
        const response = await authHttp.post<BookingResponseDTO>( "/api/purchase/book", request, { "timeout": TICKET_PAYMENT.bookingRequestTimeoutMs } );

        return response.data;
    }

    /**
     * Returns stored booking data and ticket metadata.
     *
     * @param id - FlowRide booking id.
     * @returns Booking data.
     */
    public static async getPurchaseBooking( id: string ): Promise<unknown> {
        const response = await authHttp.get<unknown>( `/api/purchase/bookings/${ id }` );

        return response.data;
    }

    /**
     * Lists the user's committed bookings — purchased itineraries with their
     * bought segments and issued tickets. Used to rebuild the local purchased
     * store on session start.
     *
     * @returns Purchased bookings, newest first.
     */
    public static async getPurchaseBookings(): Promise<PurchasedBookingDTO[]> {
        const response = await authHttp.get<PurchasedBookingDTO[]>( "/api/purchase/bookings" );

        return response.data;
    }

    /**
     * Downloads the invoice PDF of a committed booking.
     *
     * The endpoint streams the stored PDF (`Content-Disposition: inline`); the
     * blob is handed to `openInvoicePdf` (`tools/invoiceDownload.tools.ts`)
     * which opens it in a new tab on web or through the native share sheet.
     *
     * @param bookingId - FlowRide booking id (`BookingResponseDTO.bookingId`).
     * @returns Invoice PDF bytes plus the server-provided download file name (`null` when absent).
     */
    public static async getPurchaseInvoice( bookingId: string ): Promise<{
        "blob":     Blob;
        "fileName": string | null;
    }> {
        const response = await authHttp.get<Blob>( `/api/purchase/bookings/${ bookingId }/invoice`, { "responseType": "blob" } );
        const disposition = response.headers["content-disposition"] as string | undefined;
        const fileName = ( /filename="([^"]+)"/ ).exec( disposition ?? "" )?.[1] ?? null;

        return {
            "blob": response.data,
            fileName,
        };
    }

    /**
     * Lists the credit notes issued against a booking's invoice.
     *
     * One note per completed refund operation, plus a balance note when the
     * booking was fully refunded — the invoice itself is immutable. The same
     * list is also embedded as `creditNotes` in the booking DTOs; this
     * endpoint refreshes it on demand.
     *
     * @param bookingId - FlowRide booking id (`PurchasedItineraryPurchase.bookingId`).
     * @returns Credit note summaries (may be empty).
     */
    public static async getPurchaseBookingCreditNotes( bookingId: string ): Promise<CreditNoteSummaryDTO[]> {
        const response = await authHttp.get<CreditNoteSummaryDTO[]>( `/api/purchase/bookings/${ bookingId }/credit-notes` );

        return response.data;
    }

    /**
     * Downloads the PDF of a credit note issued against a booking's invoice.
     *
     * The endpoint streams the stored PDF (`Content-Disposition: inline`); the
     * blob is handed to `openCreditNotePdf` (`tools/invoiceDownload.tools.ts`)
     * which opens it in a new tab on web or through the native share sheet.
     *
     * @param bookingId - FlowRide booking id (`PurchasedItineraryPurchase.bookingId`).
     * @param creditNoteId - Credit note id (`CreditNoteSummaryDTO.id`).
     * @returns Credit note PDF bytes plus the server-provided download file name (`null` when absent).
     */
    public static async getPurchaseCreditNote( bookingId: string, creditNoteId: string ): Promise<{
        "blob":     Blob;
        "fileName": string | null;
    }> {
        const response = await authHttp.get<Blob>( `/api/purchase/bookings/${ bookingId }/credit-notes/${ creditNoteId }`, { "responseType": "blob" } );
        const disposition = response.headers["content-disposition"] as string | undefined;
        const fileName = ( /filename="([^"]+)"/ ).exec( disposition ?? "" )?.[1] ?? null;

        return {
            "blob": response.data,
            fileName,
        };
    }

    /**
     * Fetches the real-time refund eligibility and financial offer for every
     * ticket of a committed booking.
     *
     * @param bookingId - FlowRide booking id (`PurchasedItineraryPurchase.bookingId`).
     * @returns Per-ticket refund preview.
     */
    public static async getPurchaseBookingRefundPreview( bookingId: string ): Promise<BookingRefundPreviewResponseDTO> {
        const response = await authHttp.get<BookingRefundPreviewResponseDTO>( `/api/purchase/bookings/${ bookingId }/refund-preview` );

        return response.data;
    }

    /**
     * Confirms and executes the refund of one or more tickets of a committed booking.
     *
     * @param bookingId - FlowRide booking id (`PurchasedItineraryPurchase.bookingId`).
     * @param request - Selected tickets and expected refundable amounts.
     * @returns Refund outcome and updated booking tickets.
     */
    public static async postPurchaseBookingRefund(
        bookingId: string,
        request: ConfirmBookingRefundRequestDTO,
    ): Promise<BookingRefundResponseDTO> {
        const response = await authHttp.post<BookingRefundResponseDTO>(
            `/api/purchase/bookings/${ bookingId }/refund`,
            request,
            { "timeout": TICKET_PAYMENT.bookingRequestTimeoutMs },
        );

        return response.data;
    }

}
